import React, {
  FC,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
  Suspense,
} from "react";
import {
  Basketball,
  Volleyball,
  Baseball,
  IceHockey,
  Football,
  Tennis,
} from "../sports";
import * as service from "../service";
import { LANGUAGE } from "_constants";
import { CountdownTimerProvider } from "components";
import { getXY } from "utils/getXY";
import { getSide } from "utils/get-side";
import { getNormalizedStatusCode } from "utils/get-normalized-status-code";
import { isThemePreviewMode } from "utils/theme-preview-storage";
import { isRuntimeThemed } from "config/theme";

const COMPONENTS = {
  [service.SPORT.BASKETBALL]: Basketball,
  [service.SPORT.VOLLEYBALL]: Volleyball,
  [service.SPORT.BASEBALL]: Baseball,
  [service.SPORT.ICE_HOCKEY]: IceHockey,
  [service.SPORT.FOOTBALL]: Football,
  [service.SPORT.TENNIS]: Tennis,
};

const Debugger = React.lazy(() =>
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_PREVIEW === "true"
    ? import("../components/Debugger")
    : import("../components/FakeDebugger")
);

export const Simulator: FC<{
  lang?: LANGUAGE;
  eventId: string;
}> = ({ lang, eventId }) => {
  const [status, setStatus] = useState<
    service.MatchStatus | undefined | null
  >();

  const fetchStatus = useCallback(async () => {
    let socketConnection: any;
    let listener: any;

    if (!eventId) return;
    try {
      const status = await service.getStatus(eventId);
      console.log("[TRACKER WS]", {
        id: eventId,
        sc: status?.sc,
        stn: status?.stn,
      });

      listener = ({
        key,
        value,
      }: {
        key?: string;
        value: service.MatchStatus;
      }) => {
        console.log("[TRACKER WS]", {
          id: key ?? eventId,
          sc: value?.sc,
          stn: value?.stn,
        });
        setStatus({
          ...value,
        });
      };
      socketConnection = service.getSocketConnection(eventId);
      socketConnection.on("POSITION", listener);

      setStatus({
        ...status,
      });
    } catch {
      setStatus(null);
    }
    return () => {
      socketConnection.off("POSITION", listener);
    };
  }, [eventId]);

  useEffect(() => {
    if (eventId) fetchStatus();
  }, [eventId, fetchStatus]);

  if (
    process.env.NODE_ENV === "development" ||
    process.env.REACT_APP_PREVIEW === "true"
  )
    if (status === null)
      return (
        <div className="flex h-screen items-center justify-center">
          Not Found
        </div>
      );
  if (!status)
    return (
      <div className="flex h-screen items-center justify-center">Loading</div>
    );

  return <SimulatorInner status={status} lang={lang} />;
};

export const SimulatorInner: FC<{
  status: service.MatchStatus;
  lang?: LANGUAGE;
}> = ({ status: _status, lang }) => {
  const simulatorRootRef = useRef<HTMLDivElement | null>(null);
  const [x, y] = getXY(_status);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isThemePreviewMode()) return;
    let isIframe = false;
    try {
      isIframe = window.self !== window.top;
    } catch {
      isIframe = true;
    }

    if (!isIframe) return;
    const { documentElement, body } = window.document;
    documentElement.classList.add("iframe-embed");
    body.classList.add("iframe-embed");
    let observer: ResizeObserver | null = null;
    let lastHeight = 0;
    const applyHeight = (height: number) => {
      if (!Number.isFinite(height) || height <= 0) return;
      const nextHeight = Math.ceil(height);
      if (nextHeight === lastHeight) return;
      lastHeight = nextHeight;
      documentElement.style.setProperty(
        "--simulator-embed-height",
        `${nextHeight}px`
      );
      // An embedder that opted in (?theme=) sizes its iframe to our content instead of a fixed
      // height, so no empty band is left under the pitch and longer tabs are not clipped.
      if (isRuntimeThemed) {
        window.parent?.postMessage({ type: "TRACKER_HEIGHT", height: nextHeight }, "*");
      }
    };

    const target = simulatorRootRef.current;
    if (target) {
      const rect = target.getBoundingClientRect();
      applyHeight(rect.height);
      if (typeof ResizeObserver !== "undefined") {
        observer = new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target !== target) return;
            applyHeight(entry.contentRect?.height);
          });
        });
        observer.observe(target);
      }
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
      documentElement.style.removeProperty("--simulator-embed-height");
      documentElement.classList.remove("iframe-embed");
      body.classList.remove("iframe-embed");
    };
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const status = {
    ..._status,
    side: getSide(_status),
    nsc: getNormalizedStatusCode(_status),
    x,
    y,
  };

  const Component = useMemo(() => {
    if (!status) return undefined;
    return COMPONENTS[status.s];
  }, [status]);

  const showHeaderFromUrl = useMemo(() => {
    const qp = new URLSearchParams(window.location.search);
    const v = qp.get("showHeader");
    return v == null ? undefined : v !== "0";
  }, []);

  return (
    <>
      <div
        ref={simulatorRootRef}
        className="relative flex flex-col bg-simulatorBg"
      >
        <CountdownTimerProvider startTime={status.tm}>
          {Component && (
            <Component
              status={status}
              language={lang}
              {...({ showHeader: showHeaderFromUrl } as any)}
            />
          )}
        </CountdownTimerProvider>
      </div>
      <Suspense fallback={<></>}>
        <Debugger status={status} />
      </Suspense>
    </>
  );
};
